import { produce } from 'immer';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
  Log,
  UserManager,
  UserManagerSettings,
  WebStorageStateStore,
} from 'oidc-client-ts';
import { AuthProviderProps } from 'react-oidc-context';
import { authenticatedOnKey } from '../constants';
import { config } from '../global/config';
import { LOGOUT_KEY } from '../hooks/useMultiTabLogout';
import { logTrace, isTraceLogEnabled } from '../telemetry/logTrace';

// react-oidc-context swallows signinSilent errors (it only returns null), so
// forward oidc-client-ts warnings/errors to the trace log to see why a
// silent renew failed (network error, invalid_grant, ...).
const formatLogArgs = (args: unknown[]) =>
  args
    .map(a => (a instanceof Error ? `${a.name}: ${a.message}` : String(a)))
    .join(' ');

if (isTraceLogEnabled) {
  Log.setLevel(Log.WARN);
  Log.setLogger({
    debug: () => {},
    info: () => {},
    warn: (...args: unknown[]) =>
      logTrace(
        'oidc-client-ts warn',
        { message: formatLogArgs(args) },
        { severity: SeverityLevel.Warning },
      ),
    error: (...args: unknown[]) =>
      logTrace(
        'oidc-client-ts error',
        { message: formatLogArgs(args) },
        { severity: SeverityLevel.Error },
      ),
  });
}

const baseOidcSettings: UserManagerSettings = {
  authority: config.idpAuthority ?? '',
  client_id: config.idpClientId ?? '',
  redirect_uri: config.idpRedirectUri ?? '',
  post_logout_redirect_uri: config.idpPostLogoutRedirectUri ?? '',
  response_type: 'code',
  automaticSilentRenew: false,
  userStore: new WebStorageStateStore({ store: globalThis.localStorage }),
  fetchRequestCredentials: 'include',
};

const onSigninCallback = (): void => {
  localStorage.removeItem(LOGOUT_KEY);

  const redirectTo = globalThis.sessionStorage.getItem('redirectTo') || '/';
  globalThis.sessionStorage.removeItem('redirectTo');
  globalThis.localStorage.setItem(authenticatedOnKey, Date.now().toString());

  if (
    redirectTo === '/signout-oidc' ||
    redirectTo.startsWith('/signout-oidc?')
  ) {
    globalThis.location.href = '/';
  } else {
    globalThis.location.href = redirectTo;
  }
};

export const benefitUserManagerSettings: UserManagerSettings = produce(
  baseOidcSettings,
  d => {
    d.scope =
      'openid profile offline_access application_version front_office:customer';
  },
);

export const merchantsUserManagerSettings: UserManagerSettings = produce(
  baseOidcSettings,
  d => {
    d.scope = 'openid profile offline_access merchants:all';
  },
);

// Shared instance so code outside react-oidc-context can call it directly and
// see the real signinSilent error (useAuth().signinSilent swallows it).
export const benefitUserManager = new UserManager({
  ...benefitUserManagerSettings,
});

export const benefitOidcConfig: AuthProviderProps = {
  userManager: benefitUserManager,
  onSigninCallback,
};

export const merchantsOidcConfig: AuthProviderProps = {
  ...merchantsUserManagerSettings,
  onSigninCallback,
};
